import React, { useEffect } from 'react';
import '../../styles/Pagination.css';
import { clampItemsPerPage } from '../../utils/settings';

const Pagination = ({ 
  currentPage, 
  totalPages, 
  totalItems, 
  itemsPerPage, 
  onPageChange,
  onItemsPerPageChange,
  showItemsPerPage = true,
  showInfo = true,
  /** Optional node rendered on the right of the footer row (after page controls). */
  extraEnd = null,
}) => {
  useEffect(() => {
    if (!showItemsPerPage || !onItemsPerPageChange) return;
    const clamped = clampItemsPerPage(itemsPerPage);
    if (clamped !== Number(itemsPerPage)) {
      onItemsPerPageChange(clamped);
    }
  }, [itemsPerPage, onItemsPerPageChange, showItemsPerPage]);

  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      // Show all pages if total pages is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  if (totalPages <= 1 && !showItemsPerPage && !showInfo && !extraEnd) {
    return null;
  }

  return (
    <div className="pagination-container">
      {showInfo ? (
        <div className="pagination-info-cluster">
          <span className="pagination-entries-text">
            Showing {startItem} to {endItem} of {totalItems} entries.
          </span>
          {showItemsPerPage && onItemsPerPageChange ? (
            <div className="items-per-page">
              <span className="items-per-page-prefix">Show</span>
              <select
                id="itemsPerPage"
                value={itemsPerPage}
                onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
                className="pagination-items-select"
                aria-label={`Show ${itemsPerPage} entries per page`}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              {/* <span className="items-per-page-suffix">entries</span> */}
            </div>
          ) : null}
        </div>
      ) : null}
      <div
        className={`pagination-col-right${!showInfo ? ' pagination-col-right--solo' : ''}`}
      >
        {(totalPages > 1 || extraEnd) && (
          <div className="pagination-end-inner">
            {totalPages > 1 && (
              <div className="pagination-controls">
                <button
                  className="pagination-btn"
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  title="Previous"
                >
                  ‹
                </button>

                {getPageNumbers().map((page, index) => {
                  if (page === '...') {
                    return (
                      <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                        ...
                      </span>
                    );
                  }

                  return (
                    <button
                      key={page}
                      className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                      onClick={() => onPageChange(page)}
                    >
                      {page}
                    </button>
                  );
                })}

                <button
                  className="pagination-btn"
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  title="Next"
                >
                  ›
                </button>
              </div>
            )}
            {extraEnd}
          </div>
        )}
      </div>
    </div>
  );
};

export default Pagination;
